import './MessageBox.css';
import { observer } from 'mobx-react';
import { action, makeAutoObservable, observable } from 'mobx';
import { Appl } from '../../Appl';
import parse from 'html-react-parser';
import { Button } from '../actions/Button';
import { Dialog } from 'primereact/dialog';
import { X } from 'lucide-react';

export const MessageBox: React.FC = observer(() => {
    const vm = Appl.MessageBox;

    const footer = (
        <div className="flex items-center justify-center gap-x-4">
            <Button
                title={vm.acceptLabel}
                rounded={vm.isSquareCorner}
                onClick={() => vm.hide()}
            />
        </div>
    );

    const headerTemplate = (
        <div className="flex items-start bg-cream-50 border-b-2 border-orange-500 p-0 py-4 px-6 justify-between items-center w-full">
            <h3 className="text-2xl font-semibold leading-6 text-black tracking--1percent">{vm.title}</h3>
            <div className='ml-4'>
                <button type="button" className="bg-orange-500 flex focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-400 h-6 w-6 hover:text-white-500 inline-flex items-center justify-center rounded-full text-white" onClick={() => vm.hide()}>
                    <span className="sr-only">Close</span>
                    <X className="h-4 w-4" aria-hidden="true" />
                </button>
            </div>
        </div>
    );

    return (
        <Dialog
            visible={vm.visible}
            onHide={() => {
                vm.hide();
                // Use a small timeout to ensure content is cleared after animation
                setTimeout(() => vm.clearContent(), 200);
            }}
            header={headerTemplate}
            footer={footer}
            closable={false}
            draggable={false}
            style={{ width: '36rem' }}
            className={`${vm.isSquareCorner ? 'rounded-none' : 'rounded-lg'} !p-0`}
            pt={{
                root: { className: `${vm.isSquareCorner ? 'rounded-none' : 'rounded-lg'} !p-0 border-none` },
                header: { className: 'rounded-none !p-0 !block' },
                content: { className: '!p-0' }
            }}
            modal
        // dismissableMask
        >
            <div className="text-15px text-gray-800 p-6">
                {typeof (vm.message) === "string" ? parse(vm.message) : vm.message}
            </div>
        </Dialog>
    );
});

export interface IMessageBoxService {
    title: string;
    subTitle: string;
    message: string | React.ReactNode;
    visible: boolean;
    isSquareCorner?: boolean;
    acceptLabel: string;
    show(message: string | React.ReactNode, title?: string, subTitle?: string, acceptLabel?: string, isSquareCorner?: boolean): void;
    hide(): void;
    clearContent(): void;
}

export class MessageBoxService implements IMessageBoxService {

    private static _instance: MessageBoxService;
    public static get instance() {
        return this._instance ?? (this._instance = new MessageBoxService());
    }

    @observable public title = '';
    @observable public subTitle = '';
    @observable public message: string | React.ReactNode = '';
    @observable public visible = false;
    @observable public acceptLabel = 'Okay';
    @observable public isSquareCorner = false;

    constructor() {
        makeAutoObservable(this);
    }

    @action public show(message: string | React.ReactNode, title: string | undefined = 'Information', subTitle = '',
        acceptLabel = "Okay", isSquareCorner = false): void {
        this.title = title;
        this.message = message;
        this.subTitle = subTitle;
        this.visible = true;
        this.acceptLabel = acceptLabel;
        this.isSquareCorner = isSquareCorner;
    }

    @action public hide(): void {
        this.visible = false;
    }

    @action public clearContent(): void {
        this.title = '';
        this.message = '';
        this.subTitle = '';
    }
}
